/**
 * SpectatorsSection - Collapsible spectators list
 *
 * Shows all spectators watching the game, with expand/collapse functionality.
 */

import { useState } from "react";
import { SpectatorCard } from "./SpectatorCard";
import type { SpectatorInfo } from "../../types/game";

interface SpectatorsSectionProps {
  spectators: SpectatorInfo[];
}

export function SpectatorsSection({ spectators }: SpectatorsSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (spectators.length === 0) {
    return null; // Don't render if no spectators
  }

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  return (
    <div className="bg-white/5 rounded-lg p-4" data-testid="spectators-section">
      {/* Header */}
      <button
        onClick={toggleExpanded}
        className="w-full flex items-center justify-between text-left hover:bg-white/5 rounded-lg p-2 transition"
        type="button"
        data-testid="spectators-toggle"
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">👁️</span>
          <h3 className="font-semibold" data-testid="spectators-count">
            Spectators ({spectators.length})
          </h3>
        </div>
        <span className="text-sm text-gray-400">{isExpanded ? "▼" : "▶"}</span>
      </button>

      {/* Collapsed: Show preview */}
      {!isExpanded && spectators.length > 0 && (
        <div
          className="mt-2 text-sm text-gray-400 pl-2"
          data-testid="spectators-preview"
        >
          {spectators
            .slice(0, 3)
            .map((s) => s.display_name)
            .join(", ")}
          {spectators.length > 3 && ` +${spectators.length - 3} more`}
        </div>
      )}

      {/* Expanded: Show full list */}
      {isExpanded && (
        <div className="mt-4 space-y-2" data-testid="spectators-list">
          {spectators.map((spectator) => (
            <SpectatorCard key={spectator.player_id} spectator={spectator} />
          ))}
        </div>
      )}
    </div>
  );
}
